// Local replacement for @latticexyz/stash/react
// Reads from staticData + SessionPlayerContext instead of the chain stash.
import { staticData } from "./staticData";
import { useSessionPlayer, DEMO_ADDRESS } from "./SessionPlayerContext";

// The DEMO_ADDRESS PlayerTimes record — always "clear" (no timeouts active)
const DEMO_PLAYER_TIMES = {
  counterValue: DEMO_ADDRESS,
  lastVote: 0,
  lastForking: 0,
  lastRoll: 0,
  lastWriting: 0,
};

export function useRecord({ stash: _stash, table, key, defaultValue }: {
  stash: unknown;
  table: { label: string };
  key: Record<string, unknown>;
  defaultValue?: unknown;
}) {
  const { player: sessionPlayer } = useSessionPlayer();
  const tableName = table.label;
  const keyField = key ? (Object.keys(key)[0] as string) : null;
  const keyValue = key ? key[keyField!] : null;

  // Session player records take precedence when key matches demo address
  if (keyValue === DEMO_ADDRESS) {
    if (tableName === "Player") return sessionPlayer ?? defaultValue;
    if (tableName === "PlayerTimes") return sessionPlayer ? DEMO_PLAYER_TIMES : defaultValue;
  }

  if (!keyValue && keyValue !== 0) return defaultValue;

  const records = (staticData[tableName] ?? []) as Record<string, unknown>[];
  const found = records.find(r => r[keyField!] == keyValue); // == to handle number/string
  return found ?? defaultValue;
}

export function useRecords({ stash: _stash, table, keys }: {
  stash: unknown;
  table: { label: string };
  keys?: unknown[];
}) {
  const { player: sessionPlayer, sessionStories } = useSessionPlayer();
  const tableName = table.label;
  const records = (staticData[tableName] ?? []) as unknown[];

  if (tableName === "Player" && sessionPlayer) {
    // Include the session player, replacing any existing entry with the same address
    const others = (records as { counterValue: string }[]).filter(
      r => r.counterValue !== DEMO_ADDRESS
    );
    return [...others, sessionPlayer];
  }

  if (tableName === "StoryPart" && sessionStories.length > 0) {
    // Prepend session-written stories so they appear first
    return [...sessionStories, ...records];
  }

  if (keys) {
    return keys
      .map(k => (records as Record<string, unknown>[]).find(r => {
        const keyField = k && typeof k === "object" ? Object.keys(k as object)[0] : null;
        return keyField ? r[keyField] == (k as Record<string, unknown>)[keyField] : false;
      }))
      .filter(Boolean);
  }

  return records;
}

// Re-export useStash as a no-op for any code that imports it
export function useStash() {
  return undefined;
}
